/**
 * What the Share dialog shows, whoever keeps it: the catalog in the app's live room (`CatalogShare`), or the demo bots
 * in a project of this browser (`DemoShare`). The dialog renders one shape and never asks which.
 */
import type { ParticipantLook } from '../presenceSource';

export type ShareRole = 'owner' | 'editor' | 'viewer';
/** Who the link admits besides the people invited: nobody, viewers, editors. */
export type LinkAccess = 'invited' | 'view' | 'edit';
/** Why the list could not be read: the sign-in ended, the access is gone, or anything to try again. */
export type ShareFailure = 'signed-out' | 'gone' | 'failed';

/**
 * A row of «People with access». `pending`: invited, not in yet; `visitor`: in by the link and listed only while online;
 * `manageable`: the viewer may change their role or remove them.
 */
export interface SharePerson {
  id: string; name: string; email?: string; role: ShareRole; look: ParticipantLook;
  self: boolean; pending: boolean; online: boolean; visitor: boolean; manageable: boolean;
  /** An invitation by address nobody signed in with yet: it can be removed, its role not changed. */
  fixedRole?: boolean;
}

/** Someone asking the owner for a role: a guest of the project to edit, someone outside to come in. */
export interface ShareRequest { id: string; name: string; look: ParticipantLook; wants: 'editor' | 'viewer' }

/** Someone the search found: an account, or in the demo one of its people. */
export interface ShareMatch { id: string; name: string; avatar?: string }
/** Who an invitation goes to: a found account, or an address typed in full. */
export type ShareInvitee = { id: string } | { email: string };

/** `catalog`: the live room's; `demo`: the bots stand in as members; `local`: a project of this browser with nobody to share with. */
export interface ShareState {
  status: 'loading' | 'ready' | 'error'; error: ShareFailure | null; mode: 'catalog' | 'demo' | 'local';
  myRole: ShareRole; people: readonly SharePerson[]; link: LinkAccess;
  /** Why «Copy link» has nothing to copy: the catalog gives members no link; a project of this browser has none. */
  noLink: 'member' | 'local' | null;
  /** The owner's to answer; only the demo has any, the catalog has no route for them yet (docs/tasks/share-access-requests). */
  requests: readonly ShareRequest[];
  /** A guest's own request to edit: null where there is none to send (the owner, an editor, the live room). */
  editRequest: 'idle' | 'sent' | null;
}

export interface ShareSource {
  get(): ShareState;
  subscribe(listener: () => void): () => void;
  refresh(): Promise<void>;
  /** Reads the list unless it is read already or being read: a reader that only needs it, never aborting a refresh. */
  load(): Promise<void>;
  /** The address «Copy link» puts on the clipboard; rejects when there is none or it cannot be had. */
  linkUrl(): Promise<string>;
  /** The owner's changes, sent as «Apply» orders them (`draft.ts`); each rejects with the catalog's error. */
  setLink(link: LinkAccess): Promise<void>;
  setRole(id: string, role: 'editor' | 'viewer'): Promise<void>;
  remove(id: string): Promise<void>;
  search(term: string, signal?: AbortSignal): Promise<ShareMatch[]>;
  /** Sent at once, as the app does, and the list read again; rejects with `conflict` for a full project or someone already in. */
  invite(invitee: ShareInvitee, role: 'editor' | 'viewer'): Promise<void>;
  approve(id: string): Promise<void>;
  deny(id: string): Promise<void>;
  requestEdit(): Promise<void>;
  /** The viewer gives up their own access; the owner cannot. */
  leave(): Promise<void>;
  dispose(): void;
}

/** The catalog answers a shorter term 406: below this a search is not asked. */
export const MIN_SEARCH = 2;

/** Whether the search should mark someone as having access already: a link visitor in the room has none of their own. */
export function accessBadge(people: readonly SharePerson[], id: string): 'member' | 'pending' | null {
  const person = people.find(candidate => candidate.id === id);
  return !person || person.visitor ? null : person.pending ? 'pending' : 'member';
}

/** What Enter does: invites the top row only; if that row already has access it names them and invites nobody. */
export function topInvitee(matches: readonly ShareMatch[], address: string | null, people: readonly SharePerson[]): { invite: ShareInvitee } | { has: string } | null {
  const top = matches[0];
  if (top) return accessBadge(people, top.id) ? { has: top.name } : { invite: { id: top.id } };
  if (!address) return null;
  return people.some(person => !person.visitor && person.email?.toLowerCase() === address) ? { has: address } : { invite: { email: address } };
}

/** The number beside «People with access»: everyone but the owner. */
export function accessCount(state: ShareState): number {
  return state.people.filter(person => person.role !== 'owner').length;
}
